import { AcreLogoIcon, InformationIcon } from "icons"
import { GetStaticProps, NextPage } from "next"
import { Trans, useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { CurrencyInput, Input, PercentInput } from "ui"
import { numberToCurrency } from "utils"
import Layout from "../components/Layout"
import * as ModuleFlow from '../components/ModuleFlow'
import { TransCompatLink } from "../components/Navbar"
import useAcreCalculator, { CalculatorPageContent } from "../hooks/useAcreCalculator"
import useAuth from "../hooks/useAuth"
import getHomesPage from "../services/contentful/homes"
import { useTracking } from "../services/context/TrackingContext"
import { PrequalificationTypes, SavePrequalificationParams, useSavePrequalification } from "../services/customer/prequalification"
import getGlobalFlags from "../services/flags/getGlobalFlags"
import { usePreferredHomesRoute } from "../hooks/use-preferred-homes-route"
import { IBasicPageProps } from "./[slug]"

const SOLICIT_EMAIL_FLAG = true
const SAVINGS_THRESHOLD = 200
const POCKET_THRESHOLD = 5000

type CompareMortgageProps = IBasicPageProps & {
  calculator: CalculatorPageContent
}

const CompareMortgage: NextPage<CompareMortgageProps> = (props) => {
  const { t } = useTranslation(['compare', 'common'])
  const router = useRouter()
  const { track, identify } = useTracking()

  const { 
    user,
    isLoading: authLoading
  } = useAuth()

  const { 
    mutate: savePrequalification,
  } = useSavePrequalification()

  const [viewResults, setViewResults] = useState(false)

  const [purchasePrice, setPurchasePrice] = useState<number>(0)
  const [interestRate, setInterestRate] = useState<number>(0)
  const [emailAddress, setEmailAddress] = useState<string>('')

  const {
    monthlyTotalMortgage,
    mortgageTotalValue,
    saverMonthlyPayment,
    boostTotalValue,
  } = useAcreCalculator({
    interestRate,
    interestRateAcre: props.calculator.interestRateAcre,
    expectedClose: purchasePrice,
    assumptions: {
      ...props.calculator,
    }
  })

  const homesRoute = usePreferredHomesRoute()

  useEffect(() => {
    if (user) {
      setEmailAddress(user.email)
    }
  }, [user])

  const savedPerMonth = monthlyTotalMortgage - saverMonthlyPayment
  const pocketedWealth = boostTotalValue - mortgageTotalValue

  const onCompareButtonClick = () => {
    if (SOLICIT_EMAIL_FLAG && emailAddress) {
      identify({
        userId: emailAddress,
        traits: {
          budget_target_home_price: purchasePrice,
          custom_mortgage_interest_rate: interestRate
        }
      })

      const params: SavePrequalificationParams = {
        prequalification: {
          type: PrequalificationTypes.mortgageCompare,
          details: {
            targetHomePrice: purchasePrice,
            customRate: interestRate,
            emailAddress,
            saverMonthlySavings: savedPerMonth,
            boostThreeYearsEstimatedWealthIncrease: pocketedWealth
          }
        },
        emailAddress
      }
      savePrequalification(params)
    }

    setViewResults(true)

    track(makeMortgateComparisonCompletedEventTrack({
      targetHomePrice: Math.round(purchasePrice),
      mortgageInterestRate: interestRate,
      emailAddress,
      saverMonthlySavings: savedPerMonth,
      boostThreeYearWealthIncrease: pocketedWealth
    }));
  }

  return (
    <Layout>
      <ModuleFlow.Container loading={authLoading}>
        <ModuleFlow.Header
          heading={t('compare-acre')}
          backButtonOnClick={() => viewResults ? setViewResults(false) : router.back()}
          closeButtonHref={homesRoute}
        />
        <ModuleFlow.Steps currentStep={viewResults ? 2 : 1}>
          {!viewResults && (
            <>
              <ModuleFlow.Step 
                motionKey="inputs"
                className="pt-24"
                contentClassName="w-full"
                maxWidthClassName="max-w-500"
              >
                <div className="pb-[64px]">
                  <div className="flex flex-col items-center space-y-8">
                    <AcreLogoIcon
                      className="fill-tan-3 text-grey-8 w-[140px] py-8"
                      aria-hidden="true"
                    />
                    <h2 className="text-[22px] font-medium text-center">{t('see-how-acre-compares')}</h2>
                  </div>
                  <form 
                    className="flex flex-col justify-center my-32 space-y-24 lg:space-y-16 min-h-[280px] lg:min-h-[240px]" 
                    onSubmit={e => { e.preventDefault() }}
                  >
                    <CurrencyInput
                      id="homePrice"
                      label={t('inputs.target-home-price')}
                      placeholder="500,000"
                      value={purchasePrice}
                      setValue={setPurchasePrice}
                    />
                    <PercentInput 
                      id="interestRate"
                      label={t('inputs.interest-rate')}
                      placeholder={`${props.calculator.interestRateAcre}%`}
                      value={interestRate}
                      setValue={setInterestRate}
                      errorText=""
                    />
                    {SOLICIT_EMAIL_FLAG && (
                      <Input
                        id="emailAddress"
                        label={t('inputs.email-address')}
                        placeholder="name@email.com"
                        value={emailAddress}
                        onChange={(e) => {setEmailAddress(e.currentTarget.value)}}
                      />
                    )}
                  </form>
                  <div className="p-16 bg-grey-2 border border-green-1 rounded-sm text-18 font-normal text-center">
                    <p>
                      &quot;So glad we found Acre. This was the best thing that happened to us!&quot;
                    </p>
                    <p className="pt-8">
                      - Residents Tara & Alex saving $700/mo
                    </p>
                  </div>
                </div>
              </ModuleFlow.Step>
              <ModuleFlow.ActionBar
                prevStepOnClick={() => router.back()}
                nextStepOnClick={onCompareButtonClick}
                nextStepDisabled={!purchasePrice || !interestRate || (SOLICIT_EMAIL_FLAG && !emailAddress)}
                nextStepText={t('inputs.compare')}
              />
            </>
          )}
          {viewResults && (
            <>
              <ModuleFlow.Step 
                motionKey="inputs"
                contentClassName="w-full"
                maxWidthClassName="max-w-500"
              >
                <div className="pb-[64px]">
                  <div className="flex flex-col items-center space-y-8">
                    <AcreLogoIcon
                      className="fill-tan-3 text-grey-8 w-[140px] py-8"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="my-32 p-16 pt-24 min-h-[280px] lg:min-h-[240px] bg-grey-2 border border-green-1 rounded-sm">
                    {savedPerMonth > SAVINGS_THRESHOLD || pocketedWealth > POCKET_THRESHOLD ? (
                      <p className="text-20">
                        Acre&apos;s shared appreciation model offers a better financial outcome 
                        compared to your <b>{interestRate}%</b> rate.
                      </p>
                    ) : (
                      <>
                        <p className="text-20">
                          Looks like you got a great rate of <b>{interestRate}%</b>.
                        </p>
                        <p className="py-32 text-20">
                          <Trans i18nKey="base-rate-warning">
                            Make sure this is your base rate. Often lenders show a lower rate that you have to pay down with substantial fees called points.{' '}
                            <TransCompatLink
                              href="https://www.nerdwallet.com/article/mortgages/property-line-discount-points-lower-mortgage-rate"
                            >
                              Read more
                            </TransCompatLink>{' '}
                            (via Nerdwallet which is unaffiliated with Acre).
                          </Trans>
                        </p>
                      </>
                    )}
                    {savedPerMonth > SAVINGS_THRESHOLD && (
                      <div className="my-16 relative rounded p-12 sm:p-24 bg-[#E8F2EE]">
                        <p className="text-20 text-center py-12">
                          {t('results.save')}{' '}
                          <span className="text-22 text-teal-3 font-bold">
                            {numberToCurrency(savedPerMonth)}
                          </span>
                          <span className="text-18 text-teal-3"> {t('results.per-month')}*</span>
                        </p>
                      </div>
                    )}
                    {pocketedWealth > POCKET_THRESHOLD && (
                      <div className="my-16 relative rounded p-12 sm:p-24 bg-[#FFF2E8]">
                        <p className="text-20 text-center py-12 leading-6">
                          {t('results.pocket')}{' '}
                          <span className="text-22 text-tan-3 font-bold">{numberToCurrency(pocketedWealth)}</span><br />
                          <span className="text-18 text-tan-3"> {t('results.more-than-mortgage')}*</span>
                        </p>
                      </div>
                    )}
                    {(savedPerMonth > SAVINGS_THRESHOLD || pocketedWealth > POCKET_THRESHOLD) && (
                      <p className="text-14">{`* ${t('results.appreciation-disclaimer')}`}</p>
                    )}
                  </div>
                  <div className="flex items-center">
                    <InformationIcon className="h-16 w-16 shrink-0 stroke-teal-4" />
                    <p className="pl-16 text-12 text-justify">
                      <Trans i18nKey="legal-dsiclaimer">
                        Acre is not a lender. Acre&apos;s payments are estimates and based on a number of factors including{' '}
                        the prime interest rate and are therefore subject to change. Working with Acre requires{' '}
                        <TransCompatLink
                          href="/get-approved"
                          className="text-teal-4"
                        >
                          getting approved
                        </TransCompatLink>
                        .
                      </Trans>
                    </p>
                  </div>
                </div>
              </ModuleFlow.Step>
              <ModuleFlow.ActionBar
                prevStepOnClick={() => setViewResults(false)}
                nextStepText={t('results.see-how')}
                nextStepOnClick={() => {
                  const targetHomePrice = Math.round(purchasePrice);

                  track(makeCalculatorOpenedEventTrack({ targetHomePrice }))

                  router.push({
                    pathname: '/calculator',
                    query: { 
                      price: targetHomePrice,
                      interest: interestRate 
                    },
                  })
                }}
              />
            </>
          )}
        </ModuleFlow.Steps>
      </ModuleFlow.Container>
    </Layout>
  )
}

type MortgateComparisonCompletedEventTrackVariables = {
  readonly targetHomePrice: number;
  readonly mortgageInterestRate: number;
  readonly saverMonthlySavings: number;
  readonly boostThreeYearWealthIncrease: number;
  readonly emailAddress: string;
}

function makeMortgateComparisonCompletedEventTrack(variables: MortgateComparisonCompletedEventTrackVariables) {
  return {
    event: 'Mortgage Comparison Tool Completed',
    properties: {
      target_home_price: variables.targetHomePrice,
      custom_mortgage_interest_rate: variables.mortgageInterestRate,
      saver_monthly_savings: variables.saverMonthlySavings,
      boost_est_three_year_wealth_increase: variables.boostThreeYearWealthIncrease,
      email_address: variables.emailAddress,
      contact_email: variables.emailAddress
    }
  }
}

type CalculatorOpenedEventTrackVariables = {
  readonly targetHomePrice: number;
}

function makeCalculatorOpenedEventTrack(variables: CalculatorOpenedEventTrackVariables) {
  return {
    event: 'Calculator Opened',
    properties: {
      target_home_price: variables.targetHomePrice,
      link_type: 'Button',
      link_cta: 'Compare',
      link_location: 'Mortgage Comparison Tool - Bottom Nav',
      link_priority: 1,
      link_color: 'Teal'
    }
  }
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const getStaticProps: GetStaticProps = async ({ locale = '' }: any) => {
  const content = await getHomesPage()
  const globalFlags = await getGlobalFlags()

  return {
    props: {
      ...globalFlags,
      ...(await serverSideTranslations(locale, [
        'compare',
        'common',
        'calculator'
      ])),
      calculator: content.pageCalculatorCollection.items[0] ?? {},
    },
    revalidate: 120
  }
}

export default CompareMortgage
